import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';
import type { AppData } from '@ext-bookmark/shared/types';
import { getCurrentUser } from '../auth/userService';
import { getAppData } from '../../lib/storage';
import { mergeAppData } from './mergeService';
import { getTodosFromSupabase } from '../todo/supabaseStorage';

export interface SyncStatus {
  hasConflict: boolean;
  localUpdatedAt?: number;
  remoteUpdatedAt?: number;
  lastSyncAt?: number;
}

export interface SyncError {
  message: string;
  code?: string;
}

/**
 * 上传数据到 Supabase
 */
export async function uploadData(appData: AppData): Promise<{ error: SyncError | null }> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      error: { message: 'Supabase 未配置，无法上传数据' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录，无法上传数据' },
    };
  }

  try {
    const now = new Date().toISOString();

    const { data: currentData } = await supabase
      .from('user_data')
      .select('app_data')
      .eq('user_id', user.id)
      .single();

    // 在上传新数据前，先保存当前数据到历史表（如果存在历史表）
    try {
      if (currentData && currentData.app_data) {
        const { data: lastVersion } = await supabase
          .from('user_data_history')
          .select('version_number')
          .eq('user_id', user.id)
          .order('version_number', { ascending: false })
          .limit(1)
          .single();

        const nextVersion = (lastVersion?.version_number || 0) + 1;

        await supabase
          .from('user_data_history')
          .insert({
            user_id: user.id,
            app_data: currentData.app_data,
            version_number: nextVersion,
          })
          .then(({ error }) => {
            if (error && error.code !== 'PGRST116') {
              console.warn('[Sync] 保存历史版本失败（历史表可能不存在）:', error.message);
            }
          });
      }
    } catch (historyError) {
      console.debug('[Sync] 历史版本保存跳过（表可能不存在）');
    }

    // 上传到 user_data：不覆盖 todos（todos 表为主数据源）
    const remoteAppData = (currentData?.app_data || {}) as AppData;
    const appDataForUpload: AppData = {
      ...appData,
      todos: remoteAppData.todos ?? appData.todos ?? [],
    };

    const { error: dataError } = await supabase
      .from('user_data')
      .upsert(
        {
          user_id: user.id,
          app_data: appDataForUpload,
          updated_at: now,
        },
        {
          onConflict: 'user_id',
        }
      );

    if (dataError) {
      console.error('[Sync] 上传数据失败:', dataError);
      return {
        error: {
          message: dataError.message || '上传数据失败',
          code: dataError.code,
        },
      };
    }

    // 更新 profile 的 last_sync_at
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        last_sync_at: now,
        updated_at: now,
      })
      .eq('id', user.id);

    if (profileError) {
      console.error('[Sync] 更新同步时间失败:', profileError);
      // 不影响主流程，只记录错误
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Sync] 上传数据异常:', error);
    return {
      error: {
        message: error.message || '上传数据时发生未知错误',
      },
    };
  }
}

/**
 * 从 Supabase 下载数据
 * @param merge 是否与本地数据合并（默认 true，用于无感同步）
 */
export async function downloadData(merge: boolean = true): Promise<{ data: AppData | null; error: SyncError | null }> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      data: null,
      error: { message: 'Supabase 未配置，无法下载数据' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      data: null,
      error: { message: '用户未登录，无法下载数据' },
    };
  }

  try {
    const { data, error } = await supabase
      .from('user_data')
      .select('app_data, updated_at')
      .eq('user_id', user.id)
      .single();

    if (error) {
      // 如果数据不存在，返回空数据而不是错误
      if (error.code === 'PGRST116') {
        return {
          data: null,
          error: null,
        };
      }

      console.error('[Sync] 下载数据失败:', error);
      return {
        data: null,
        error: {
          message: error.message || '下载数据失败',
          code: error.code,
        },
      };
    }

    if (!data || !data.app_data) {
      return {
        data: null,
        error: null,
      };
    }

    const remoteData = data.app_data as AppData;

    // 从 todos 表获取最新的待办事项（因为待办事项主要存储在 todos 表中）
    try {
      const { todos: todosFromTable, error: todosError } = await getTodosFromSupabase();
      
      if (!todosError && todosFromTable && todosFromTable.length > 0) {
        // 使用 todos 表中的数据（更准确，支持分组协作）
        remoteData.todos = todosFromTable;
      } else if (remoteData.todos && remoteData.todos.length > 0) {
        // 如果 todos 表没有数据，使用 user_data 中的数据（向后兼容）
        console.debug('[Sync] 使用 user_data 中的待办事项数据');
      }
    } catch (error) {
      console.warn('[Sync] 获取待办事项失败，使用 user_data 中的数据:', error);
      // 如果获取失败，继续使用 user_data 中的数据
    }

    // 如果需要合并，智能合并本地和云端数据
    if (merge) {
      const localData = await getAppData();
      const mergedData = mergeAppData(localData, remoteData);
      
      return {
        data: mergedData,
        error: null,
      };
    }

    // 不合并，直接返回云端数据（用于手动下载）
    return {
      data: remoteData,
      error: null,
    };
  } catch (error: any) {
    console.error('[Sync] 下载数据异常:', error);
    return {
      data: null,
      error: {
        message: error.message || '下载数据时发生未知错误',
      },
    };
  }
}

/**
 * 获取最后同步时间
 */
export async function getLastSyncTime(): Promise<Date | null> {
  if (!isSupabaseAvailable() || !supabase) {
    return null;
  }

  const user = await getCurrentUser();
  if (!user) {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('last_sync_at')
      .eq('id', user.id)
      .single();

    if (error || !data || !data.last_sync_at) {
      return null;
    }

    return new Date(data.last_sync_at);
  } catch (error) {
    console.error('[Sync] 获取最后同步时间失败:', error);
    return null;
  }
}

/**
 * 检查同步状态（是否有冲突）
 */
export async function checkSyncStatus(localUpdatedAt: number): Promise<SyncStatus> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      hasConflict: false,
      localUpdatedAt,
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      hasConflict: false,
      localUpdatedAt,
    };
  }

  try {
    // 获取远程数据的更新时间
    const { data, error } = await supabase
      .from('user_data')
      .select('updated_at')
      .eq('user_id', user.id)
      .single();

    if (error || !data || !data.updated_at) {
      // 远程没有数据，无冲突
      return {
        hasConflict: false,
        localUpdatedAt,
      };
    }

    const remoteUpdatedAt = new Date(data.updated_at).getTime();
    const lastSyncAt = await getLastSyncTime();
    const lastSyncTime = lastSyncAt ? lastSyncAt.getTime() : undefined;

    // 如果本地更新时间晚于最后同步时间，且远程更新时间也晚于最后同步时间，则可能有冲突
    const hasConflict =
      lastSyncTime !== undefined &&
      localUpdatedAt > lastSyncTime &&
      remoteUpdatedAt > lastSyncTime &&
      localUpdatedAt !== remoteUpdatedAt;

    return {
      hasConflict,
      localUpdatedAt,
      remoteUpdatedAt,
      lastSyncAt: lastSyncTime,
    };
  } catch (error) {
    console.error('[Sync] 检查同步状态失败:', error);
    return {
      hasConflict: false,
      localUpdatedAt,
    };
  }
}
